<template>
  <div class="lz" :class="{ 'lz--collapsed': collapsed }">
    <div class="lz__bar" @click="collapsed = !collapsed">
      <span class="lz__dot" :class="featureOn ? 'is-on' : 'is-off'"></span>
      <strong>Lease</strong>
      <span class="lz__count">{{ enrollIds.length }} curso(s)</span>
      <span class="lz__chev">{{ collapsed ? '▸' : '▾' }}</span>
    </div>

    <div v-show="!collapsed" class="lz__body">
      <div class="lz__meta">
        <div>feature: <b>{{ featureOn ? 'ON' : 'OFF (kill switch)' }}</b></div>
        <div>studentId ativo: <b>{{ studentId || '—' }}</b></div>
        <div>TTL: <b>{{ ttlDays }}d</b> + <b>{{ graceDays }}d</b> grace</div>
      </div>

      <div v-if="!enrollIds.length" class="lz__empty">Nenhum curso com download</div>

      <div v-for="id in enrollIds" :key="id" class="lz__enroll">
        <div class="lz__enroll-head">
          <span class="lz__state" :class="'is-' + stateOf(id)">{{ stateOf(id) }}</span>
          <span class="lz__name">{{ nameOf(id) }}</span>
          <span class="lz__eid">#{{ id }}</span>
        </div>

        <div class="lz__grid">
          <span>entitlement</span>
          <b :class="blocked(id) ? 'lz--bad' : 'lz--ok'">{{ blocked(id) ? 'BLOQUEADO' : 'liberado' }}</b>
          <span>dias p/ expirar</span><b>{{ days(id) == null ? '—' : days(id) }}</b>
          <span>JS validUntil</span><b>{{ fmt(claimJs(id).validUntil) }}</b>
          <span>JS graceUntil</span><b>{{ fmt(claimJs(id).graceUntil) }}</b>
          <span>JS revokedAt</span><b>{{ fmt(claimJs(id).revokedAt) }}</b>
          <span>nativo validUntil</span><b :class="nativeCls(id)">{{ fmt(claimNative(id).validUntil) }}</b>
          <span>nativo graceUntil</span><b :class="nativeCls(id)">{{ fmt(claimNative(id).graceUntil) }}</b>
          <span>nativo revokedAt</span><b :class="nativeCls(id)">{{ fmt(claimNative(id).revokedAt) }}</b>
          <span>nativo studentId</span><b :class="nativeCls(id)">{{ claimNative(id).studentId || '—' }}</b>
        </div>

        <div class="lz__row">
          <small>lease</small>
          <button @click="renew(id)">renovar</button>
          <button @click="force(id, 'soon')">15s</button>
          <button @click="force(id, 'grace')">grace</button>
          <button @click="force(id, 'expired')">expirar</button>
          <button @click="force(id, 'revoked')">revogar</button>
          <button class="lz__ghost" @click="clear(id)">limpar</button>
        </div>
        <div class="lz__row">
          <small>entitlement</small>
          <button @click="sim(id, 'blocked')">bloquear (pagto)</button>
          <button @click="sim(id, 'absent')">ausência</button>
          <button @click="sim(id, 'freed')">liberar</button>
        </div>
      </div>

      <div class="lz__actions">
        <button class="lz__ghost" @click="refresh">atualizar</button>
        <button class="lz__ghost" @click="hydrate">hydrate nativo</button>
      </div>
    </div>
  </div>
</template>

<script>
import { getLease, setLease, isLeaseFeatureEnabled, getDefaultConfig } from '@/services/leaseService';

const MIN = 60 * 1000;

export default {
  name: 'LeaseDebugPanel',
  data: () => ({ collapsed: false, native: {}, timer: null }),
  computed: {
    featureOn () { return isLeaseFeatureEnabled(); },
    studentId () { return this.$store.state.user.currentUser && this.$store.state.user.currentUser.studentId; },
    ttlDays () { return Math.round(getDefaultConfig().ttlMs / (24 * 60 * MIN)); },
    graceDays () { return Math.round(getDefaultConfig().graceMs / (24 * 60 * MIN)); },
    downloads () { return this.$store.state.download.downloads || {}; },
    enrollIds () { return Object.keys(this.downloads); },
    courseNames () {
      const map = {};
      (this.$store.getters['download/downloadedCourses'] || []).forEach(c => {
        map[String(c.enrollId)] = c.courseName || c.name;
      });
      return map;
    }
  },
  methods: {
    nameOf (id) { return this.courseNames[String(id)] || '(sem nome)'; },
    stateOf (id) { return this.$store.getters['lease/playableState'](id); },
    days (id) { return this.$store.getters['lease/daysUntilExpiry'](id); },
    blocked (id) { return Boolean((this.$store.state.lease.blockedEntitlements || {})[String(id)]); },
    claimJs (id) {
      const e = this.$store.state.lease.leases[String(id)];
      return (e && e.claim) || {};
    },
    claimNative (id) { return this.native[String(id)] || {}; },
    nativeCls (id) { return this.native[String(id)] ? '' : 'lz--muted'; },
    fmt (ts) {
      if (ts == null) return '—';
      try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
    },
    // Monta claim com datas relativas ao agora — força grace/expired sem esperar
    // o TTL real. studentId = o logado, senão o bind derruba pra 'expired'.
    makeClaim (id, validOffset, graceOffset, revoked) {
      const now = Date.now();
      return {
        v: 1,
        enrollId: String(id),
        studentId: String(this.studentId || ''),
        issuedAt: now - MIN,
        validUntil: now + validOffset,
        graceUntil: now + graceOffset,
        ttlMs: 0,
        graceMs: 0,
        revokedAt: revoked ? now : null
      };
    },
    async renew (id) { await this.$store.dispatch('lease/touch', { enrollId: id }); this.refresh(); },
    async force (id, kind) {
      if (kind === 'revoked') { await this.$store.dispatch('lease/revoke', { enrollId: id, reason: 'debug' }); return this.refresh(); }
      let claim;
      if (kind === 'soon') claim = this.makeClaim(id, 15000, 15000, false);   // válido por 15s → depois expired
      else if (kind === 'grace') claim = this.makeClaim(id, -MIN, 2 * MIN, false); // passou validUntil, ainda no grace
      else claim = this.makeClaim(id, -2 * MIN, -MIN, false);                 // passou graceUntil → expired
      await setLease(id, claim);
      this.$store.commit('lease/SET_LEASE', { enrollId: id, claim });
      this.refresh();
    },
    async clear (id) { await this.$store.dispatch('lease/clearLease', { enrollId: id }); this.refresh(); },
    // Simula uma lista fresca de enrolls e roda o caminho REAL do syncEntitlements
    // (revoga bloqueados/ausentes, renova liberados). Mantém os OUTROS downloads
    // liberados pra o teste isolar o enroll alvo.
    async sim (id, mode) {
      const ids = this.enrollIds;
      let items; let total;
      if (mode === 'absent') {
        items = ids.filter(e => String(e) !== String(id)).map(e => ({ id: e, pendingPayment: false }));
        total = items.length; // backend "removeu" o alvo → não conta
      } else {
        items = ids.map(e => ({ id: e, pendingPayment: mode === 'blocked' && String(e) === String(id) }));
        total = items.length;
      }
      await this.$store.dispatch('lease/syncEntitlements', { items, total });
      this.refresh();
    },
    async hydrate () { await this.$store.dispatch('lease/hydrateFromNative'); this.refresh(); },
    async refresh () {
      const next = {};
      for (const id of this.enrollIds) {
        try { next[String(id)] = await getLease(id); } catch { next[String(id)] = null; }
      }
      this.native = next;
    }
  },
  mounted () {
    this.refresh();
    this.timer = setInterval(this.refresh, 2000);
  },
  beforeDestroy () { clearInterval(this.timer); }
};
</script>

<style scoped>
.lz {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 9998;
  width: 300px;
  max-width: calc(100vw - 24px);
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: #e6edf3;
  background: rgba(13, 17, 23, 0.96);
  border: 1px solid #30363d;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}
.lz__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  cursor: pointer;
  user-select: none;
  background: #161b22;
  flex: none;
}
.lz__dot { width: 8px; height: 8px; border-radius: 50%; }
.lz__dot.is-on { background: #2ea043; }
.lz__dot.is-off { background: #6e7681; }
.lz__count { margin-left: auto; color: #8b949e; }
.lz__chev { color: #8b949e; }
.lz__body { padding: 8px 10px; border-top: 1px solid #21262d; overflow-y: auto; }
.lz__meta { color: #8b949e; margin-bottom: 8px; line-height: 1.5; }
.lz__meta b { color: #c9d1d9; }
.lz__empty { color: #6e7681; padding: 4px 0; }
.lz__enroll { border-top: 1px solid #21262d; padding: 8px 0; }
.lz__enroll-head { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.lz__name { color: #c9d1d9; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.lz__eid { color: #6e7681; }
.lz__state { padding: 1px 6px; border-radius: 4px; text-transform: uppercase; font-size: 10px; flex: none; }
.lz__state.is-valid { background: #23863633; color: #7ee787; }
.lz__state.is-grace { background: #9e6a0333; color: #e3b341; }
.lz__state.is-expired, .lz__state.is-revoked, .lz__state.is-unknown { background: #da363333; color: #ff7b72; }
.lz__grid { display: grid; grid-template-columns: auto 1fr; gap: 1px 8px; margin-bottom: 6px; }
.lz__grid span { color: #6e7681; }
.lz__grid b { color: #c9d1d9; text-align: right; word-break: break-all; }
.lz--ok { color: #7ee787 !important; }
.lz--bad { color: #ff7b72 !important; }
.lz--muted { color: #4d5560 !important; }
.lz__row { display: flex; align-items: center; gap: 4px; margin-top: 4px; flex-wrap: wrap; }
.lz__row small { color: #6e7681; width: 100%; }
.lz__row button, .lz__actions button {
  padding: 3px 7px;
  border: 1px solid #30363d;
  border-radius: 5px;
  background: #21262d;
  color: #e6edf3;
  font: inherit;
  cursor: pointer;
}
.lz__ghost { background: transparent !important; color: #8b949e !important; }
.lz__actions { display: flex; gap: 6px; margin-top: 10px; border-top: 1px solid #21262d; padding-top: 8px; }
</style>
